import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import {
  ExportEntityOption,
  ItemSearchOptionField,
  ItemSearchOptionFieldOption
} from 'src/app/components/filter-list/models/filterClasses';
import { Actions, Workflow } from 'src/app/models';
import { ComponentIds } from 'src/app/models/component-ids';
import { ReportDataSetFieldDataType } from 'src/app/models/custom-report';
import { DataEntitiesExportParameters } from 'src/app/models/exportOption';
import {
  FilterBuilderOutput,
  FilterBuilderParam,
  SearchFilterBuilderOutput,
  translateFilterBuilderToLegacy
} from 'src/app/models/filter-builder';
import { DataSet } from 'src/app/models/report-data-set';
import { ShapefileValidationCriteria } from 'src/app/models/scheduled-export';
import {
  ContractorService,
  ExportFormat,
  SecurityService,
  Utilities,
  WorkflowContextService,
  WorkflowService
} from 'src/app/services';

@Component({
  selector: 'app-export-workflow',
  templateUrl: './export-workflow.component.html',
  styleUrls: ['./export-workflow.component.css'],
  standalone: false
})
export class ExportWorkflowComponent implements OnInit, OnDestroy {
  @ViewChild('tabset', { static: true }) tabset: any;

  id = ComponentIds.DATA_EXPORT;
  dsId = ComponentIds.DATA_SET_EXPORT;
  name = new UntypedFormControl('');
  selDS = new UntypedFormControl('');
  form: any;
  exportId: string;
  SelectedWorkflow: Workflow;
  graph: mxGraph;
  exportOptions: { value: string; label: string }[];
  availableColumns: ExportEntityOption[];
  exportColumns: string[] = [];
  showArchived: false;
  workflows: Workflow[];
  selectedEntityValues: string[]; // DataEntity[];
  s: string[];
  exporting = false;
  exportDisabled = true;
  showDownloadLink = false;
  title: string;
  shapefileValidationCriteria = new ShapefileValidationCriteria();

  public dsParams: FilterBuilderParam[];
  public dsExportColumns: string[];

  CONTRACTORS_EXPORT_ITEM = 'contractors';
  CONTRACTOR_TYPE_PARAM_ID = 'contractorType';

  filterTypes = [
    {
      name: 'contains'
    },
    {
      name: 'is'
    },
    {
      name: 'in'
    },
    {
      name: 'range'
    }
  ];

  public params: FilterBuilderParam[] = [
    {
      name: 'App #',
      id: 'applicationNumber'
    },
    {
      name: 'Description',
      id: 'description'
    },
    {
      name: 'Applicant',
      id: 'applicantsName'
    },
    {
      name: 'Date Created',
      id: 'dateCreated',
      inputType: 'date',
      types: ['is', 'range']
    },
    {
      name: 'Submitter',
      id: 'submitterId',
      types: ['is']
    },
    {
      name: 'Status',
      id: 'status',
      types: ['is', 'in'],
      options: [
        {
          name: 'Not Started',
          value: '0'
        },
        {
          name: 'In Progress',
          value: '1'
        },
        {
          name: 'Completed',
          value: '2'
        },
        {
          name: 'Voided',
          value: '3'
        },
        {
          name: 'Rejected',
          value: '4'
        },
        {
          name: 'Discarded',
          value: '5'
        }
      ]
    },
    {
      name: 'Parcel Address',
      id: 'parcelAddress'
    },
    {
      name: 'Parcel City',
      id: 'parcelCity'
    },
    {
      name: 'Parcel Id',
      id: 'parcelId'
    },
    {
      name: 'Parcel Owner',
      id: 'parcelOwner'
    },
    {
      name: 'Current Activity',
      id: 'currentActivity'
    }
  ];
  public filters: ItemSearchOptionField[];
  public dsFilters: ItemSearchOptionFieldOption[];
  dsStaticFilters: ItemSearchOptionField[];
  staticFilters: ItemSearchOptionField[] = [];

  private contractorTypeParam: FilterBuilderParam;

  private clientSubscription: Subscription;

  public exportFormat: ExportFormat;
  public useExcelFormat: boolean;

  public dataSets: DataSet[];
  public dataSetId: string;

  private filterForm: UntypedFormGroup;
  private canCreateDataSet: boolean;

  constructor(
    private sanitizer: DomSanitizer,
    private _workflowSvc: WorkflowService,
    private _context: WorkflowContextService,
    public route: ActivatedRoute,
    private _fb: UntypedFormBuilder,
    private _contractorSvc: ContractorService,
    public router: Router,
    private _ref: ChangeDetectorRef,
    private _securitySvc: SecurityService
  ) {}

  exportEntityOptions: {
    name: string;
    label: string;
    includeInExport: boolean;
  }[];

  dsColumnOptions: {
    name: string;
    label: string;
    includeInExport: boolean;
  }[];

  columns: ExportEntityOption[];

  link = document.createElement('a');

  async loadWorkflows() {
    this.showArchived = false;
    const workflows = await this._workflowSvc.getExportWorkflows().toPromise();

    const options = workflows.map(w => {
      return {
        id: w.id,
        name: w.name
      };
    });

    options.push({
      id: this.CONTRACTORS_EXPORT_ITEM,
      name: 'Contractors'
    });
    options.sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );
    this.exportOptions = options.map(o => ({ value: o.id, label: o.name }));

    const contractorTypes = await this._contractorSvc
      .getContractorRegistrationTypes(this._context.client?.id)
      .toPromise();

    this.contractorTypeParam = {
      name: 'Contractor Type',
      id: this.CONTRACTOR_TYPE_PARAM_ID,
      types: ['is'],
      options: contractorTypes.map(ct => ({ name: ct.name, value: ct.id }))
    };
  }

  sanitize(url: string) {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  exportExcel() {
    this.useExcelFormat = true;
    this.exportFormat = ExportFormat.Excel;
    this.exportData();
  }

  exportCsv() {
    this.useExcelFormat = false;

    this.exportFormat = ExportFormat.CSV;
    this.exportData();
  }

  exportShapefile() {
    this.useExcelFormat = false;
    this.exportFormat = ExportFormat.ShapeFile;
    this.exportData();
  }

  exportData() {
    if (this.form.valid) {
      this.showDownloadLink = false;
      this.exporting = true;
      this.exportDisabled = true;

      if (!this.dataSetId) {
        const filtersWithWorkflowId = [...this.filters, ...this.staticFilters];

        const exportParameters: DataEntitiesExportParameters = {
          dataEntities: this.columns,
          additionalFilters: filtersWithWorkflowId,
          exportFormat: this.exportFormat,
          useExcelFormat: this.useExcelFormat
        };

        this._workflowSvc
          .exportEntities(this.exportId, exportParameters)
          .subscribe(response => {
            if (this.useExcelFormat) {
              const blob = new Blob([response], {
                type:
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              });
              const selectedOption = this.exportOptions.find(
                o => o.value === this.exportId
              );
              this.link.href = window.URL.createObjectURL(blob);
              // this.link.download = this.name.value.name + '.xlsx';
              this.link.download =
                (selectedOption?.label ?? 'export') + '.xlsx';
            } else {
              if (this.exportFormat === ExportFormat.ShapeFile) {
                const blob = new Blob([response], { type: 'application/zip' });
                this.link.href = window.URL.createObjectURL(blob);
                // this.link.download = this.name.value.name + '.zip';
                const selectedOption = this.exportOptions.find(
                  o => o.value === this.exportId
                );
                this.link.download =
                  (selectedOption?.label ?? 'export') + '.zip';
              } else {
                const blob = new Blob([response], { type: 'text/csv' });
                this.link.href = window.URL.createObjectURL(blob);
                // this.link.download = this.name.value.name + '.csv';
                const selectedOption = this.exportOptions.find(
                  o => o.value === this.exportId
                );
                this.link.download =
                  (selectedOption?.label ?? 'export') + '.csv';
              }
            }
            this.exporting = false;
            this.exportDisabled = false;
            this.showDownloadLink = true;
          });
      } else {
        this._workflowSvc
          .exportDataSet({
            dataSetId: this.dataSetId,
            exportFormat: this.useExcelFormat
              ? ExportFormat.Excel
              : ExportFormat.CSV,
            columnsToInclude: this.dsExportColumns,
            filterInput: {
              inputValues: this.dsFilters
            }
          })
          .subscribe(response => {
            const ds = this.dataSets.find(ds => ds.id == this.dataSetId);
            if (this.useExcelFormat) {
              const blob = new Blob([response], {
                type:
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              });
              // const url = window.URL.createObjectURL(blob);
              this.link.href = window.URL.createObjectURL(blob);
              this.link.download = ds.name + '.xlsx';
            } else {
              const blob = new Blob([response], { type: 'text/csv' });
              // const link = document.createElement('a');
              this.link.href = window.URL.createObjectURL(blob);
              this.link.download = ds.name + '.csv';
            }
            this.exporting = false;
            this.exportDisabled = false;
            this.showDownloadLink = true;
          });
      }
    }
  }

  ngOnInit() {
    if (this._context.client) {
      this.loadWorkflows();
      // this.loadDataSets();
    } else {
      this.clientSubscription = this._context.client$.subscribe(() => {
        this.loadWorkflows();
        // this.loadDataSets();
      });
    }

    this._securitySvc
      .isLoginEntitled(Actions.MANAGE_DATASETS)
      .subscribe(result => {
        this.canCreateDataSet = result;
      });

    this.form = new UntypedFormGroup({
      firstName: new UntypedFormControl()
    });
    this.form.addControl(
      'controlType',
      this._fb.control(false, [Validators.nullValidator])
    );
    this.form.addControl('name', this.name);
    this.form.addControl('selDS', this.selDS);
    this.route.params.subscribe(params => {
      if (params['workflowId']) {
        this.exportId = params['workflowId'];
      }
    });
    this.setAvailableColumns();
  }

  setAvailableColumns() {
    this.availableColumns = this.columns;
  }

  ngOnDestroy() {
    if (this.clientSubscription) {
      this.clientSubscription.unsubscribe();
      this.clientSubscription = null;
    }
  }

  loadDataSets() {
    this._workflowSvc
      .getPublishedDataSets(this._context.client.id)
      .subscribe(dataSets => {
        this.dataSets = dataSets;
      });
  }

  filterFormChanged(e) {
    this.filterForm = null;
    this.filterForm = e;
  }

  switchDataSet() {
    if (this.selDS.value == 'create') {
      this.router.navigate([
        '/admin/jurisdiction',
        this._context.client.id,
        'reports',
        'data-set',
        Utilities.EMPTY_GUID
      ]);
    } else {
      this.dataSetId = this.selDS.value;
    }
  }

  switchWorkflowOption(): void {
    this.exportId = this.name.value;

    if (this.exportId) {
      // clear out the dataset fields
      this.dataSetId = null;
      this.dsColumnOptions = null;
      this.dsExportColumns = null;
      this.dsFilters = null;
      this.dsParams = null;
      this.dsStaticFilters = null;

      this._ref.detectChanges();

      const newExportIdFilter = new ItemSearchOptionField({
        title: 'exportId',
        options: [
          new ItemSearchOptionFieldOption({
            title: '',
            selected: true,
            filterText: '',
            searchText: this.exportId,
            strOperator: 'is',
            searchOptionMethod: ''
          })
        ]
      });

      let existingExportIdFilter: ItemSearchOptionField;

      if (this.staticFilters && this.staticFilters.length > 0) {
        existingExportIdFilter = this.staticFilters.find(
          f => f.title === 'exportId'
        );
        if (existingExportIdFilter) {
          Object.assign(existingExportIdFilter, newExportIdFilter);
        }
      } else {
        this.staticFilters.push(newExportIdFilter);
      }

      // get the list of entities
      let entityObs: Observable<ExportEntityOption[]> = null;

      if (this.exportId !== this.CONTRACTORS_EXPORT_ITEM) {
        entityObs = this._workflowSvc.getExportEntityOptions(this.exportId);

        this.params = this.params.filter(
          param => param.id !== this.CONTRACTOR_TYPE_PARAM_ID
        );
      } else {
        entityObs = this._contractorSvc.getExportEntityOptions();

        entityObs = this._contractorSvc.getExportEntityOptions();
        this.params.push(this.contractorTypeParam);
      }

      this.exportEntityOptions = [];

      this.exportEntityOptions = [];

      entityObs.subscribe(e => {
        this.exportDisabled = false;

        this.exportEntityOptions = e.map(v => {
          return {
            name: v.name,
            label: v.header,
            includeInExport: v.isGeoJSON === true ? false : true
          };
        });

        this.shapefileValidationCriteria.geoJSONEntities = Object.assign(
          {},
          ...e.filter(v => v.isGeoJSON === true).map(v => ({ [v.name]: v }))
        );

        this.columns = e
          .filter(v => v.isGeoJSON !== true) // default include non-geojson
          .map(v => ({
            name: v.name,
            header: v.header,
            includeInExport: true,
            checked: true,
            isGeoJSON: v.isGeoJSON
          }));

        this.showDownloadLink = false;
      });

      this.title =
        this.exportOptions.find(eo => eo.value === this.exportId).label || '';
    } else {
      this.clearWorkflowParameters();
    }
  }

  filtersChanged(filters: FilterBuilderOutput) {
    this.filters = translateFilterBuilderToLegacy(filters);
  }

  dataSetParamsChanged(params: FilterBuilderParam[]) {
    this.dsParams = params;
  }

  clearWorkflowParameters() {
    this.exportId = null;
    this.staticFilters = [];
    this.filters = null;
    this.exportEntityOptions = null;
    this.title = null;
  }

  dataSetLoaded(ds: DataSet) {
    if (ds) {
      this.clearWorkflowParameters();

      this.dsColumnOptions = ds.publishedDataSetConfig.fields.map(f => {
        return {
          name: f.name,
          label: f.resultHeader || f.name,
          includeInExport: true
        };
      });

      this.shapefileValidationCriteria.geoJSONEntities = Object.assign(
        {},
        ...ds.publishedDataSetConfig.fields
          .filter(v => v.dataType === ReportDataSetFieldDataType.GISData)
          .map(v => ({ [v.name]: v }))
      );
      this.dsStaticFilters = [
        {
          title: 'dataSetId',
          options: [
            {
              title: '',
              filterText: ds.id,
              searchText: ds.id,
              strOperator: 'is',
              selected: true,
              searchOptionMethod: ''
            }
          ]
        }
      ];
    }
  }

  dataSetFiltersChanged(filters: SearchFilterBuilderOutput) {
    const filterKeys = Object.keys(filters);

    this.dsFilters = filterKeys.flatMap(f => {
      return filters[f].map(filter => {
        return new ItemSearchOptionFieldOption({
          title: filter.id,
          strOperator: filter.type,
          searchText: filter.value,
          filterText: filter.value,
          inputType: filter.inputType
        });
      });
    });
  }
  dataSetColumnsChanged(columns: string[]) {
    this.dsExportColumns = columns;
    this.shapefileValidationCriteria.isGeoJSONSelected = false;
    this.shapefileValidationCriteria.showShapeFieldError = false;
  }

  columnsChanged(exportColumns: string[]) {
    this.shapefileValidationCriteria.isGeoJSONSelected = false;
    this.columns = this.exportEntityOptions
      .filter(eeo => exportColumns.includes(eeo.name))
      .map(eeo => ({
        name: eeo.name,
        header: eeo.label,
        includeInExport: true,
        checked: true,
        isGeoJSON:
          this.shapefileValidationCriteria.geoJSONEntities?.[eeo.name] != null
      }));
    let geoDataEntityCount = 0;

    this.exportEntityOptions.forEach(eeo => {
      if (
        exportColumns.includes(eeo.name) &&
        this.shapefileValidationCriteria.geoJSONEntities[eeo.name]
      ) {
        geoDataEntityCount += 1;
        this.shapefileValidationCriteria.isGeoJSONSelected = true;
      }

      eeo.includeInExport = exportColumns.includes(eeo.name);
    });

    this.exportEntityOptions = this.exportEntityOptions.slice();

    if (
      this.columns.length - geoDataEntityCount >
        this.shapefileValidationCriteria.allowedExportFieldCount &&
      this.shapefileValidationCriteria.isGeoJSONSelected
    ) {
      this.shapefileValidationCriteria.showShapeFieldError = true;
    } else {
      this.shapefileValidationCriteria.showShapeFieldError = false;
    }
  }

  onExportColumnsChange(cols: string[]) {
    this.exportColumns = cols;
  }

  onColumnsChanged(columnSet: { columns: any[]; exportColumns: any[] }) {
    // Update visible columns
    const visibleKeys = columnSet.columns.map(c => c.name);

    this.columns = this.availableColumns.map(col => ({
      ...col,
      hidden: !visibleKeys.includes(col.name)
    }));

    // Update export columns
    this.exportColumns = columnSet.exportColumns.map(c => c.name);
  }
}
